"use client";

import { useEffect, useState } from "react";
import { Compass } from "lucide-react";
import { useAuth } from "@wtc/ui";

/**
 * "How did this person find us" on the contact detail view.
 *
 * The single most useful thing marketing attribution gives a SALESPERSON: knowing a
 * prospect arrived from the Schooley Mitchell cost-savings ad rather than a Google
 * search changes the opening line of the call.
 *
 * Renders NOTHING when there is no attribution — which is most contacts (everyone
 * created before the analytics rollout, everyone added by hand, everyone imported).
 * An empty card saying "unknown" would be noise on hundreds of records, and worse, a
 * card that said "direct" would be a guess presented as a fact.
 */

interface Attribution {
  first_touch_source?: string;
  first_touch_medium?: string;
  first_touch_campaign?: string;
  first_touch_referrer?: string;
  first_touch_landing_path?: string;
  first_touch_at?: string;
  last_touch_source?: string;
  last_touch_campaign?: string;
  capture_source?: string;
  capture_form_id?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDate(value: string | undefined): string | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Human phrase for the first touch, or null when there is nothing to say. */
function describeFirstTouch(attribution: Attribution): string | null {
  const { first_touch_source, first_touch_medium } = attribution;
  if (first_touch_source && first_touch_medium) {
    return `${first_touch_source} / ${first_touch_medium}`;
  }
  if (first_touch_source) return first_touch_source;
  // No UTMs but a referrer is still an answer: they came from somewhere.
  if (attribution.first_touch_referrer) return attribution.first_touch_referrer;
  return null;
}

export function ContactFirstTouch({ contactId }: { contactId: string }) {
  const { getAuthToken } = useAuth();
  const [attribution, setAttribution] = useState<Attribution | null>(null);

  useEffect(() => {
    if (!UUID.test(contactId)) return;
    const controller = new AbortController();

    const load = async (): Promise<void> => {
      try {
        const token = await getAuthToken();
        const response = await fetch(`/api/marketing/analytics/contact/${contactId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!response.ok) return;
        const body: { attribution?: Attribution | null } = await response.json();
        if (body.attribution) setAttribution(body.attribution);
      } catch {
        // Silent by design: this is supplementary context on a record that renders
        // fine without it. A failed fetch must not put an error box on the contact.
      }
    };

    void load();
    return () => controller.abort();
  }, [contactId, getAuthToken]);

  const firstTouch = attribution ? describeFirstTouch(attribution) : null;
  if (!attribution || !firstTouch) return null;

  const capturedOn = formatDate(attribution.first_touch_at);
  const lastTouch =
    attribution.last_touch_source && attribution.last_touch_source !== attribution.first_touch_source
      ? attribution.last_touch_source
      : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="flex items-start gap-2">
        <Compass className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
        <div className="min-w-0 space-y-1 text-sm">
          <p className="text-gray-900 dark:text-gray-100">
            <span className="font-medium">Found us via</span> {firstTouch}
            {attribution.first_touch_campaign ? (
              <span className="text-gray-600 dark:text-gray-400">
                {" "}
                · {attribution.first_touch_campaign}
              </span>
            ) : null}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {[
              capturedOn ? `First seen ${capturedOn}` : null,
              attribution.capture_source ? `captured on ${attribution.capture_source}` : null,
              attribution.first_touch_landing_path
                ? `landed on ${attribution.first_touch_landing_path}`
                : null,
              lastTouch ? `most recently via ${lastTouch}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>
    </div>
  );
}
